import { PageSkeleton } from '@euroasiann/shared/ui';
/**
 * ProfilePage — admin can update their own name, phone, and location.
 * Email and role stay read-only (identity / access control).
 */

import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Modal } from "../../components/shared/Modal";
import { ProfileSettings } from "./ProfileSettings";
import {
  MdPerson,
  MdEmail,
  MdSecurity,
  MdAccessTime,
  MdEdit,
  MdCheckCircle,
  MdCancel,
  MdCameraAlt,
  MdLock,
  MdBusiness,
  MdCode,
  MdLocationOn,
  MdPhone,
  MdLanguage,
  MdGppGood,
  MdShield,
  MdErrorOutline,
  MdHistory,
  MdTerminal,
  MdAccountBalance,
} from "react-icons/md";

import { cn } from "../../lib/utils";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/shared/Toast";
import { authenticatedFetch } from "../../lib/api";

// API Base URL
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// -------------------------- TYPES --------------------------
interface BackendUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  portalType: string;
  role: string;
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
  mfaEnabled?: boolean;
}

interface BackendProfile {
  phone?: string;
  department?: string;
  designation?: string;
  location?: string;
}

interface UserApiResponse {
  success: boolean;
  data: {
    user: BackendUser;
    profile: BackendProfile | null;
  };
  message?: string;
}

interface DisplayProfile {
  firstName: string;
  lastName: string;
  email: string;
  portalType: string;
  role: string;
  permissions: string[];
  isActive: boolean;
  mfaEnabled: boolean;
  organization: string;
  department: string;
  designation: string;
  primaryRoleType: string;
  primaryTechStack: string[];
  environmentAccess: string[];
  lastLogin: string;
  createdAt: string;
  updatedAt: string;
  phone?: string;
  location?: string;
  timeZone: string;
  language: string;
  theme: "light" | "dark" | "system";
}

// -------------------------- MAIN COMPONENT --------------------------
export function ProfilePage() {
  const {
    user: currentUser,
    permissions: authPermissions,
    isAuthenticated,
    loading: authLoading,
  } = useAuth();

  const [searchParams] = useSearchParams();
  const settingsTabParam = searchParams.get('tab');
  const settingsTab =
    settingsTabParam === 'password' ||
    settingsTabParam === 'security' ||
    settingsTabParam === 'language' ||
    settingsTabParam === 'timezone' ||
    settingsTabParam === 'date-format' ||
    settingsTabParam === 'banking'
      ? settingsTabParam
      : null;

  const toast = useToast();

  const [profile, setProfile] = useState<DisplayProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Editable fields
  const [editFirstName, setEditFirstName] = useState<string>("");
  const [editLastName, setEditLastName] = useState<string>("");
  const [editPhone, setEditPhone] = useState<string>("");
  const [editLocation, setEditLocation] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [bankingDetails, setBankingDetails] = useState<{
    accountName?: string;
    accountNumber?: string;
    bankName?: string;
    branchName?: string;
    swiftCode?: string;
    iban?: string;
    currency?: string;
  } | null>(null);

  const fallback = {
    organization: "EuroAsian Maritime ERP",
    primaryRoleType: "Staff / Developer",
    primaryTechStack: ["React", "Node.js", "TypeScript", "Tailwind CSS"],
    environmentAccess: ["DEV", "STAGING"],
    mfaEnabled: true,
    timeZone: "IST (UTC+5:30)",
    language: "English (US)",
    theme: "system" as const,
  };

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated || !currentUser) {
      setError("You must be logged in to view your profile.");
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("accessToken");
        if (!token) throw new Error("No authentication token found");

        if (!currentUser._id) throw new Error("User ID not available");

        const response = await axios.get<UserApiResponse>(
          `${API_URL}/api/v1/admin/users/${currentUser._id}`, // or /tech/users/ if different endpoint
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.data.success) {
          throw new Error(response.data.message || "Failed to load profile");
        }

        const { user, profile: userProfile } = response.data.data;

        const displayProfile: DisplayProfile = {
          firstName: user.firstName || "Unknown",
          lastName: user.lastName || "",
          email: user.email || "Not available",
          portalType: user.portalType || "tech",
          role: user.role || "User",
          permissions: authPermissions || [],
          isActive: user.isActive ?? true,
          mfaEnabled: user.mfaEnabled ?? fallback.mfaEnabled,
          organization: fallback.organization,
          department: userProfile?.department || "Not specified",
          designation: userProfile?.designation || "Not specified",
          primaryRoleType: fallback.primaryRoleType,
          primaryTechStack: fallback.primaryTechStack,
          environmentAccess: fallback.environmentAccess,
          lastLogin: user.lastLogin || "Never",
          createdAt: user.createdAt || new Date().toISOString(),
          updatedAt: user.updatedAt || user.createdAt || new Date().toISOString(),
          phone: userProfile?.phone || "",
          location: userProfile?.location || "",
          timeZone: fallback.timeZone,
          language: fallback.language,
          theme: fallback.theme,
        };

        setProfile(displayProfile);
        setEditFirstName(displayProfile.firstName || "");
        setEditLastName(displayProfile.lastName || "");
        setEditPhone(displayProfile.phone || "");
        setEditLocation(displayProfile.location || "");
      } catch (err: any) {
        const errorMsg =
          err.response?.data?.message ||
          err.message ||
          "Failed to load your profile. Please check your connection and try again.";
        setError(errorMsg);
        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [currentUser, authPermissions, isAuthenticated, authLoading, toast]);

  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await authenticatedFetch('/api/v1/admin/settings/payment-settings');
        if (!response.ok) return;
        const result = await response.json();
        if (!cancelled && result.success && result.data?.data) {
          setBankingDetails(result.data.data);
        }
      } catch {
        /* banking is optional on profile */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated]);

  const handleSaveChanges = async () => {
    if (!profile || !currentUser?._id) return;

    const firstName = editFirstName.trim();
    const lastName = editLastName.trim();
    if (!firstName || !lastName) {
      toast.error("First name and last name are required");
      return;
    }

    try {
      setSaving(true);

      const token = localStorage.getItem("accessToken");
      if (!token) throw new Error("Authentication required");

      const updateData = {
        firstName,
        lastName,
        phone: editPhone.trim() || undefined,
        location: editLocation.trim() || undefined,
      };

      const response = await axios.patch(
        `${API_URL}/api/v1/admin/users/${currentUser._id}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || response.data.error || "Update failed");
      }

      // Re-fetch full profile (PATCH returns user doc only, not nested profile)
      const refreshed = await axios.get<UserApiResponse>(
        `${API_URL}/api/v1/admin/users/${currentUser._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (refreshed.data.success) {
        const { user, profile: userProfile } = refreshed.data.data;
        setProfile((prev) =>
          prev
            ? {
                ...prev,
                firstName: user.firstName || firstName,
                lastName: user.lastName || lastName,
                phone: userProfile?.phone || editPhone.trim() || "",
                location: userProfile?.location || editLocation.trim() || "",
                updatedAt: user.updatedAt || prev.updatedAt,
              }
            : null
        );
      } else {
        setProfile((prev) =>
          prev
            ? {
                ...prev,
                firstName,
                lastName,
                phone: editPhone.trim() || "",
                location: editLocation.trim() || "",
              }
            : null
        );
      }

      toast.success("Profile updated successfully!");
      setIsEditModalOpen(false);
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Failed to save changes";
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return <PageSkeleton variant="cards" />;
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-8">
        <div className="max-w-lg w-full rounded-2xl border border-red-500/30 bg-[hsl(var(--card))] p-10 text-center shadow-2xl">
          <MdErrorOutline className="mx-auto text-red-500" size={64} />
          <h2 className="mt-6 text-3xl font-bold text-[hsl(var(--foreground))]">
            Error Loading Profile
          </h2>
          <p className="mt-4 text-lg text-[hsl(var(--muted-foreground))]">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-10 rounded-xl bg-[#0088cc] px-10 py-4 font-semibold text-white shadow-lg transition hover:bg-[#0077b3]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const fullName = `${profile.firstName} ${profile.lastName}`;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] p-6">
      <div className="w-full space-y-6">
        {/* HERO SECTION */}
        <div className="rounded-xl border border-[#0088cc]/25 bg-[hsl(var(--card))] shadow-sm dark:border-[#0088cc]/20 dark:bg-[#0f2744]">
          <div className="p-6">
            <div className="flex flex-col items-start gap-6 md:flex-row">
              <div className="relative">
                <div className="flex h-24 w-24 items-center justify-center rounded-xl border border-[#0088cc]/30 bg-[#0088cc]/15 text-2xl font-semibold text-[#0088cc] dark:bg-[#0088cc]/20 dark:text-[#7dd3fc]">
                  {profile.firstName[0]}{profile.lastName[0]}
                </div>
              </div>

              <div className="flex-1 space-y-3">
                <div>
                  <div className="mb-2 flex flex-wrap items-center gap-3">
                    <h1 className="text-2xl font-semibold text-[hsl(var(--foreground))]">{fullName}</h1>
                    <span className="rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2.5 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                      Verified
                    </span>
                    {profile.isActive && (
                      <span className="rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2.5 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                        Active
                      </span>
                    )}
                  </div>
                  <p className="text-base text-[hsl(var(--muted-foreground))]">
                    {profile.role} • {profile.department}
                  </p>
                </div>
                {profile.designation && (
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    {profile.designation}
                  </p>
                )}
              </div>

              <div>
                <button
                  onClick={() => {
                    setEditFirstName(profile.firstName || "");
                    setEditLastName(profile.lastName || "");
                    setEditPhone(profile.phone || "");
                    setEditLocation(profile.location || "");
                    setIsEditModalOpen(true);
                  }}
                  className="flex items-center gap-2 rounded-xl bg-[#0088cc] px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-[#0088cc]/25 transition-colors hover:bg-[#0077b3]"
                >
                  <MdEdit size={16} /> Edit
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* MAIN GRID — full profile details (matches admin profile layout) */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-8">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <StatCard
                label="Primary Tech"
                value={profile.primaryTechStack[0]}
                icon={<MdCode className="text-[#0088cc]" size={18} />}
              />
              <StatCard
                label="Security Clearance"
                value="Level 3"
                icon={<MdShield className="text-[#0088cc]" size={18} />}
              />
              <StatCard
                label="Environment Access"
                value={`${profile.environmentAccess.length} Zones`}
                icon={<MdTerminal className="text-[#0088cc]" size={18} />}
              />
              <StatCard
                label="System Uptime"
                value="99.98%"
                icon={<MdHistory className="text-[#0088cc]" size={18} />}
              />
            </div>

            {/* Contact & Organizational */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <ProfileSection title="Contact Information" icon={<MdPerson className="text-[#0088cc]" />}>
                <DataField label="Work Email" value={profile.email} copyable />
                <DataField label="Phone" value={profile.phone || "Not provided"} />
                <DataField label="Location" value={profile.location || "Not specified"} />
                <DataField label="Time Zone" value={profile.timeZone} />
                <DataField label="Preferred Language" value={profile.language} />
              </ProfileSection>

              <ProfileSection title="Organizational Structure" icon={<MdBusiness className="text-[#0088cc]" />}>
                <DataField label="Organization" value={profile.organization} />
                <DataField label="Department" value={profile.department} />
                <DataField label="Designation" value={profile.designation} />
                <DataField label="Portal Type" value={profile.portalType.toUpperCase()} />
                <DataField
                  label="Account Status"
                  value={profile.isActive ? "Active" : "Inactive"}
                  badge={
                    profile.isActive
                      ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30"
                      : "bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/30"
                  }
                />
              </ProfileSection>
            </div>

            {/* Banking details (read-only snapshot from payment settings) */}
            <ProfileSection title="Banking Details" icon={<MdAccountBalance className="text-[#0088cc]" />}>
              <div className="grid grid-cols-1 gap-x-8 gap-y-1 sm:grid-cols-2">
                <DataField label="Account Name" value={bankingDetails?.accountName || "Not set"} />
                <DataField label="Account Number" value={bankingDetails?.accountNumber || "Not set"} copyable={Boolean(bankingDetails?.accountNumber)} />
                <DataField label="Bank Name" value={bankingDetails?.bankName || "Not set"} />
                <DataField label="Branch Name" value={bankingDetails?.branchName || "Not set"} />
                <DataField label="SWIFT / BIC" value={bankingDetails?.swiftCode || "Not set"} copyable={Boolean(bankingDetails?.swiftCode)} />
                <DataField label="IBAN" value={bankingDetails?.iban || "Not set"} copyable={Boolean(bankingDetails?.iban)} />
                <DataField label="Currency" value={bankingDetails?.currency || "Not set"} />
              </div>
            </ProfileSection>

            {/* Access Permissions */}
            <ProfileSection title="Access Permissions" icon={<MdSecurity className="text-[#0088cc]" />}>
              <div className="flex flex-wrap gap-2">
                {profile.permissions.length > 0 ? (
                  profile.permissions.map((perm) => (
                    <span
                      key={perm}
                      className="rounded-md border border-[#0088cc]/25 bg-[#0088cc]/10 px-3 py-1.5 text-xs font-medium text-[hsl(var(--foreground))] dark:bg-[#0088cc]/15 dark:text-[#7dd3fc]"
                    >
                      {perm}
                    </span>
                  ))
                ) : (
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    No permissions loaded.
                  </p>
                )}
              </div>
              <div className="mt-4 border-t border-[#0088cc]/20 pt-4">
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  Permissions managed via RBAC policy. Contact Security team for changes.
                </p>
              </div>
            </ProfileSection>

            {/* Technical Expertise */}
            <ProfileSection title="Technical Expertise" icon={<MdCode className="text-[#0088cc]" />}>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {profile.primaryTechStack.map((tech) => (
                  <div
                    key={tech}
                    className="rounded-md border border-[#0088cc]/25 bg-[#0088cc]/10 p-3 text-center dark:bg-[#0088cc]/10"
                  >
                    <p className="text-sm font-medium text-[hsl(var(--foreground))]">{tech}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <p className="mb-2 text-sm font-medium text-[hsl(var(--foreground))]">
                  Environment Access:
                </p>
                <div className="flex flex-wrap gap-2">
                  {profile.environmentAccess.map((env) => (
                    <span
                      key={env}
                      className="rounded-md bg-[#0088cc] px-3 py-1 text-xs font-medium text-white"
                    >
                      {env}
                    </span>
                  ))}
                </div>
              </div>
            </ProfileSection>
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-6 lg:col-span-4">
            <div className="rounded-xl border border-[#0088cc]/25 bg-[hsl(var(--card))] p-6 dark:border-[#0088cc]/20 dark:bg-[#0f2744]">
              <div className="mb-4 flex items-start justify-between">
                <div>
                  <h3 className="flex items-center gap-2 text-base font-semibold text-[hsl(var(--foreground))]">
                    <MdGppGood size={18} className="text-[#0088cc]" /> Security Score
                  </h3>
                  <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">Account health</p>
                </div>
                <span className="rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2.5 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">Excellent</span>
              </div>
              <div className="mb-4 text-4xl font-bold text-[hsl(var(--foreground))]">97</div>
              <div className="mb-4 h-2 w-full rounded-full bg-[#0088cc]/15">
                <div className="h-2 w-[97%] rounded-full bg-emerald-500" />
              </div>
              <ul className="space-y-2 text-sm text-[hsl(var(--muted-foreground))]">
                <li className="flex items-center gap-2"><MdCheckCircle className="text-emerald-500" size={16} /> MFA Enabled</li>
                <li className="flex items-center gap-2"><MdCheckCircle className="text-emerald-500" size={16} /> Strong Password</li>
                <li className="flex items-center gap-2"><MdCheckCircle className="text-emerald-500" size={16} /> Normal Activity</li>
              </ul>
            </div>

            <ProfileSection title="Security Controls" icon={<MdLock className="text-[#0088cc]" />}>
              <div className="space-y-4">
                <ToggleField label="Two-Factor Authentication" enabled={profile.mfaEnabled} />
                <ToggleField label="IP Address Whitelisting" enabled={true} />
                <ToggleField label="Session Timeout Enforcement" enabled={true} />
                <ToggleField label="Biometric Login" enabled={false} />
              </div>
            </ProfileSection>

            <ProfileSection title="Audit Trail" icon={<MdAccessTime className="text-[#0088cc]" />}>
              <div className="space-y-4">
                <AuditItem label="Account Created" date={profile.createdAt} description="Initial system provisioning" />
                <AuditItem label="Last Profile Update" date={profile.updatedAt} description="Minor details updated" />
                <AuditItem label="Most Recent Login" date={profile.lastLogin} description="From trusted device" />
              </div>
            </ProfileSection>
          </div>
        </div>

        {/* Account settings accordion — deep-linked from header menu (?tab=) */}
        <div id="account-settings" className="scroll-mt-24">
          <ProfileSettings activeTab={settingsTab} />
        </div>
      </div>

      {/* EDIT MODAL */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Update Profile Information" size="large">
        <div className="space-y-6 p-8">
          <div className="rounded-xl border border-[#0088cc]/30 bg-[#0088cc]/10 p-4">
            <p className="text-sm text-[hsl(var(--foreground))]">
              You can update your name, phone, and location. Email and role are managed by system administrators.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-[hsl(var(--foreground))]">First Name</label>
              <input
                type="text"
                value={editFirstName}
                onChange={(e) => setEditFirstName(e.target.value)}
                required
                className="w-full rounded-xl border border-[#0088cc]/25 bg-[hsl(var(--background))] px-4 py-3 text-[hsl(var(--foreground))] transition focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-[hsl(var(--foreground))]">Last Name</label>
              <input
                type="text"
                value={editLastName}
                onChange={(e) => setEditLastName(e.target.value)}
                required
                className="w-full rounded-xl border border-[#0088cc]/25 bg-[hsl(var(--background))] px-4 py-3 text-[hsl(var(--foreground))] transition focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-[hsl(var(--foreground))]">Email</label>
              <input
                type="email"
                value={profile.email}
                disabled
                className="w-full cursor-not-allowed rounded-xl border border-[#0088cc]/25 bg-[#0088cc]/10 px-4 py-3 text-[hsl(var(--muted-foreground))] dark:bg-[#0b1c30]"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-[hsl(var(--foreground))]">Role</label>
              <input
                type="text"
                value={profile.role}
                disabled
                className="w-full cursor-not-allowed rounded-xl border border-[#0088cc]/25 bg-[#0088cc]/10 px-4 py-3 text-[hsl(var(--muted-foreground))] dark:bg-[#0b1c30]"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-[hsl(var(--foreground))]">Phone Number</label>
              <input
                type="tel"
                value={editPhone}
                onChange={(e) => setEditPhone(e.target.value)}
                placeholder="Optional"
                className="w-full rounded-xl border border-[#0088cc]/25 bg-[hsl(var(--background))] px-4 py-3 text-[hsl(var(--foreground))] transition focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-[hsl(var(--foreground))]">Location</label>
              <input
                type="text"
                value={editLocation}
                onChange={(e) => setEditLocation(e.target.value)}
                placeholder="Optional"
                className="w-full rounded-xl border border-[#0088cc]/25 bg-[hsl(var(--background))] px-4 py-3 text-[hsl(var(--foreground))] transition focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20"
              />
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-6">
            <button
              onClick={() => setIsEditModalOpen(false)}
              disabled={saving}
              className="rounded-xl border border-[#0088cc]/25 px-6 py-3 font-medium text-[hsl(var(--foreground))] transition-colors hover:bg-[#0088cc]/10"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveChanges}
              disabled={saving}
              className={cn(
                "flex items-center gap-2 rounded-xl px-8 py-3 font-semibold shadow-md transition-colors",
                saving
                  ? "cursor-not-allowed bg-[#0088cc]/30 text-[hsl(var(--muted-foreground))]"
                  : "bg-[#0088cc] text-white hover:bg-[#0077b3]"
              )}
            >
              {saving ? (
                <>
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// -------------------------- SUB-COMPONENTS --------------------------
function ProfileSection({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-[#0088cc]/25 bg-[hsl(var(--card))] p-6 dark:border-[#0088cc]/20 dark:bg-[#0f2744]">
      <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-[hsl(var(--foreground))]">
        <span className="rounded-md bg-[#0088cc]/15 p-1.5">{icon}</span>
        {title}
      </h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function DataField({ label, value, copyable, badge }: { label: string; value: string; copyable?: boolean; badge?: string }) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[#0088cc]/80 dark:text-[#7dd3fc]/80">{label}</p>
      <div className={cn("flex items-center justify-between", copyable && "group cursor-pointer")}>
        {badge ? (
          <span className={cn("rounded px-2 py-0.5 text-xs font-medium", badge)}>{value}</span>
        ) : (
          <p className={cn("text-sm font-medium text-[hsl(var(--foreground))]", copyable && "transition-colors group-hover:text-[#0088cc]")}>
            {value}
          </p>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-[#0088cc]/25 bg-[hsl(var(--card))] p-4 dark:border-[#0088cc]/20 dark:bg-[#0f2744]">
      <div className="rounded-md bg-[#0088cc]/15 p-2">{icon}</div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-[#0088cc]/80 dark:text-[#7dd3fc]/80">{label}</p>
        <p className="mt-0.5 text-base font-semibold text-[hsl(var(--foreground))]">{value}</p>
      </div>
    </div>
  );
}

function ToggleField({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-[hsl(var(--foreground))]">{label}</span>
      {enabled ? <MdCheckCircle className="text-emerald-500" size={18} /> : <MdCancel className="text-[hsl(var(--muted-foreground))]" size={18} />}
    </div>
  );
}

function AuditItem({ label, date, description }: { label: string; date: string; description?: string }) {
  const parsed = date && date !== 'Never' ? new Date(date) : null;
  const valid = parsed && !Number.isNaN(parsed.getTime());
  const formattedDate = valid
    ? parsed!.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
    : date || '—';
  const formattedTime = valid
    ? parsed!.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div className="border-l-2 border-[#0088cc]/40 py-2 pl-4">
      <p className="text-sm font-medium text-[hsl(var(--foreground))]">{label}</p>
      <p className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))]">
        {formattedTime ? `${formattedDate} at ${formattedTime}` : formattedDate}
      </p>
      {description && <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">{description}</p>}
    </div>
  );
}

